import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';
import vulnApi from '../../services/vulnApi';
import useVulnFilters from '../../hooks/useVulnFilters';
import VulnFilterBar from '../../components/vuln/VulnFilterBar';
import CreateScanModal from '../../components/vuln/CreateScanModal';

function VulnScans() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { customerId, setCustomerId } = useVulnFilters();

  const pollRef = useRef(null);

  const fetchScans = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await vulnApi.getScans(customerId || undefined);
      setScans(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load scans');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchScans();
  }, [customerId]);

  useEffect(() => {
    const hasActive = scans.some((s) => s.status === 'running' || s.status === 'paused');
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => fetchScans(true), 5000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [scans]);

  const handleExecute = async (scanId) => {
    try {
      setNotification({ type: 'info', text: 'Starting scan...' });
      await vulnApi.executeScan(scanId);
      setNotification({ type: 'success', text: 'Scan started successfully' });
      fetchScans();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handlePause = async (scanId) => {
    try {
      await vulnApi.pauseScan(scanId);
      setNotification({ type: 'success', text: 'Scan paused' });
      fetchScans();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handleResume = async (scanId) => {
    try {
      await vulnApi.resumeScan(scanId);
      setNotification({ type: 'success', text: 'Scan resumed' });
      fetchScans();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handleStop = async (scanId) => {
    try {
      await vulnApi.stopScan(scanId);
      setNotification({ type: 'success', text: 'Scan stopped' });
      fetchScans();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const handleDelete = async (scanId) => {
    try {
      await vulnApi.deleteScan(scanId);
      setNotification({ type: 'success', text: 'Scan deleted' });
      setDeleteTarget(null);
      fetchScans();
    } catch (err) {
      setNotification({ type: 'error', text: err.message });
    }
  };

  const modeBadge = (mode) => {
    const styles = {
      external: 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400',
      internal: 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400',
      mixed: 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400',
    };
    return styles[mode] || styles.external;
  };

  const statusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-100 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400',
      running: 'bg-amber-100 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400',
      paused: 'bg-violet-100 dark:bg-violet-400/20 text-violet-600 dark:text-violet-400',
      failed: 'bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400',
      pending: 'bg-slate-100 dark:bg-slate-400/20 text-slate-600 dark:text-slate-400',
    };
    return styles[status] || styles.pending;
  };

  const isActive = (status) => status === 'running' || status === 'paused';

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">

            {/* Page header */}
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                  Vulnerability Scans
                </h1>
              </div>
              <VulnFilterBar
                customerId={customerId}
                onCustomerChange={setCustomerId}
              >
                <button
                  onClick={() => setShowCreate(true)}
                  className="btn bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded text-sm"
                >
                  + New Scan
                </button>
              </VulnFilterBar>
            </div>

            {/* Notification */}
            {notification && (
              <div
                className={`px-4 py-3 rounded-sm mb-6 ${
                  notification.type === 'success'
                    ? 'bg-emerald-100 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400'
                    : notification.type === 'error'
                    ? 'bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400'
                    : 'bg-blue-100 dark:bg-blue-400/20 text-blue-600 dark:text-blue-400'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{notification.text}</span>
                  <button onClick={() => setNotification(null)} className="ml-4 text-current opacity-70 hover:opacity-100">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-rose-100 dark:bg-rose-400/20 text-rose-600 dark:text-rose-400 px-4 py-3 rounded-sm mb-6">
                {error}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
              </div>
            )}

            {!loading && !error && (
              <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
                <div className="overflow-x-auto">
                  <table className="table-auto w-full">
                    <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Name</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Type</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Mode</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Status</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Progress</th>
                        <th className="px-5 py-3 whitespace-nowrap text-left">Created</th>
                        <th className="px-5 py-3 whitespace-nowrap text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                      {scans.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                            No scans yet. Click "+ New Scan" to create one.
                          </td>
                        </tr>
                      ) : (
                        scans.map((scan) => (
                          <tr key={scan.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                            <td className="px-5 py-3 whitespace-nowrap font-medium text-slate-800 dark:text-slate-100">
                              {scan.name}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap capitalize text-slate-600 dark:text-slate-300">
                              {scan.scan_type}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <span className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 text-xs capitalize ${modeBadge(scan.scan_mode)}`}>
                                {scan.scan_mode}
                              </span>
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(scan.status)}`}>
                                {scan.status}
                              </span>
                              {scan.status === 'failed' && scan.error_message && (
                                <div className="text-xs text-rose-500 dark:text-rose-400 mt-1 max-w-xs truncate" title={scan.error_message}>
                                  {scan.error_message}
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap">
                              {scan.status === 'running' ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-24 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                                    <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${scan.progress || 0}%` }} />
                                  </div>
                                  <span className="text-xs text-slate-500">{scan.progress || 0}%</span>
                                </div>
                              ) : (
                                <span className="text-slate-500">{scan.progress != null ? `${scan.progress}%` : '-'}</span>
                              )}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                              {scan.created_at ? new Date(scan.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end space-x-1">
                                {scan.status === 'completed' && (
                                  <button
                                    onClick={() => navigate(`/vuln/findings?scan_id=${scan.id}`)}
                                    className="text-indigo-500 hover:text-indigo-600 rounded-full"
                                    title="View results"
                                  >
                                    <span className="sr-only">Results</span>
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                      <path d="M16 8C9.4 8 4 14 4 16s5.4 8 12 8 12-6 12-8-5.4-8-12-8zm0 14c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z" />
                                      <circle cx="16" cy="16" r="3" />
                                    </svg>
                                  </button>
                                )}
                                {scan.status === 'running' ? (
                                  <button
                                    onClick={() => handlePause(scan.id)}
                                    className="text-amber-500 hover:text-amber-600 rounded-full"
                                    title="Pause scan"
                                  >
                                    <span className="sr-only">Pause</span>
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                      <path d="M12 10h3v12h-3zM17 10h3v12h-3z" />
                                    </svg>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => scan.status === 'paused' ? handleResume(scan.id) : handleExecute(scan.id)}
                                    className="text-indigo-500 hover:text-indigo-600 rounded-full"
                                    title={scan.status === 'paused' ? 'Resume scan' : 'Execute scan'}
                                  >
                                    <span className="sr-only">{scan.status === 'paused' ? 'Resume' : 'Execute'}</span>
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                      <path d="M12 10v12l10-6z" />
                                    </svg>
                                  </button>
                                )}
                                {isActive(scan.status) && (
                                  <button
                                    onClick={() => handleStop(scan.id)}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full"
                                    title="Stop scan"
                                  >
                                    <span className="sr-only">Stop</span>
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
                                      <path d="M10 10h12v12H10z" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  onClick={() => setDeleteTarget(scan)}
                                  className="text-rose-500 hover:text-rose-600 rounded-full"
                                  title="Delete scan"
                                >
                                  <span className="sr-only">Delete</span>
                                  <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                                    <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                                    <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <CreateScanModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(scan) => {
          setScans(prev => [scan, ...prev]);
          setNotification({ type: 'success', text: `Scan "${scan.name}" created` });
        }}
        customerId={customerId}
      />

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          ></div>
          <div className="relative max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 fill-current text-amber-500" viewBox="0 0 24 24">
                  <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                  Confirm Deletion
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Are you sure you want to delete <span className="font-medium text-slate-800 dark:text-slate-200">"{deleteTarget.name}"</span>? All associated findings will also be removed. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-md hover:bg-rose-700 transition-colors"
                onClick={() => handleDelete(deleteTarget.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VulnScans;
